// The piano's ChordDiagram equivalent (#236): a read-only keyboard strip with
// the voicing's keys sounding. Pure SVG so it scales and prints like every
// other diagram; geometry follows PianoInput's proportions (black keys 60%
// width, 62% height, centred on the boundary) so the two keyboards read as
// one instrument.
//
// THE iREAL READING (user ruling Aug 2026, from their reference screenshot —
// "too busy and hard to read on mobile"): sounding keys wear a DOT and
// nothing else, MIDDLE C is the grey key, and the key lines are heavy enough
// to read at phone size. This supersedes two earlier rulings that were each
// right in their own moment and wrong together: the sounding-key SHADE
// (added so a voicing reads at arm's length) and the lettered middle C in
// its own lane (added because a tinted key beside shaded keys read as a
// second kind of sounding note). With the shade gone, the grey C is the only
// tinted key on the diagram, so it reads unambiguously as the reference —
// which is exactly iReal's design, and why their tint works.
//
// The marks stay theme-proof BY CONSTRUCTION rather than by tuning: the dot
// is the key's OPPOSITE ink at full strength and the grey anchor is that
// same ink at low opacity, so a white key's marks always darken and a black
// key's always lighten, whatever a palette does to the tokens. (The first
// two passes at this component picked a colour — `--accent`, then a
// hand-measured ochre — and both were wrong in a theme where `--accent` is a
// near-black BACKGROUND. A mark derived from its own ground cannot be.)
//
//
// Geometry (window, key size, aspect) lives in pianoKeyboardGeometry.ts.

import { IS_BLACK, KEYBOARD_H, KEY_W, keyboardRange, whitesIn } from './pianoKeyboardGeometry';

const MIDDLE_C = 60;

/** The middle-C anchor: light enough to read as furniture, not a note. */
const ANCHOR_OPACITY = 0.16;

export interface PianoKeyboardDiagramProps {
  /** Pressed keys (sounding MIDI). */
  midis: number[];
  /** Range override (inclusive). Defaults to the sounding span padded by
   * KEY_PAD white keys either side — the trimmed iReal window. Pass a shared
   * range only where a COLUMN of diagrams must align (the Lab's voice-leading
   * walk); the sheet deliberately lets each chord take its own window and the
   * grey middle C, where it falls in range, carries the octave orientation. */
  low?: number;
  high?: number;
  /** Rendered width in px; the height follows the window's aspect. */
  width?: number;
  /** Rendered height in px; the WIDTH follows the window's aspect — the
   * natural way to size a strip whose key count varies. Ignored when
   * `width` is given. With neither, the SVG carries no size attributes and
   * CSS decides (the sheet: `--keyboard-height` × `--keyboard-aspect`). */
  height?: number;
  label?: string;
  /** Shade middle C grey as the octave anchor (default on; drawn only when
   * C4 is actually in range, and it gets no dot unless it is played). */
  showMiddleC?: boolean;
  onClick?: () => void;
  /** Per-key taps — the place-the-notes editor. The diagram is then a group
   * of key buttons rather than one button; `onClick` is ignored. */
  onKeyClick?: (midi: number) => void;
}

export function PianoKeyboardDiagram({
  midis,
  low,
  high,
  width,
  height,
  label,
  showMiddleC = true,
  onClick,
  onKeyClick,
}: PianoKeyboardDiagramProps) {
  const range = keyboardRange(midis, low, high);
  const whites = whitesIn(range);
  const whiteW = KEY_W;
  const H = KEYBOARD_H;
  const viewW = whites.length * whiteW;
  const size =
    width !== undefined
      ? { width, height: (width * H) / viewW }
      : height !== undefined
        ? { width: (height * viewW) / H, height }
        : {};
  const pressed = new Set(midis);
  const whiteIndexOf = (midi: number) => whites.filter((w) => w < midi).length;

  const blacks: number[] = [];
  for (let m = range.low; m <= range.high; m += 1) {
    if (IS_BLACK[((m % 12) + 12) % 12]) blacks.push(m);
  }

  const anchorC = showMiddleC && whites.includes(MIDDLE_C);
  // Each key a button when the editor asks for key taps: role, tab stop,
  // Enter/Space, the same debt the whole-diagram button pays.
  const keyProps = (midi: number) =>
    onKeyClick
      ? {
          role: 'button' as const,
          tabIndex: 0,
          'aria-label': `key ${midi}`,
          'aria-pressed': pressed.has(midi),
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
            onKeyClick(midi);
          },
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onKeyClick(midi);
            }
          },
        }
      : {};

  // A clickable diagram IS a button, not a picture that happens to react:
  // the sheet reads interactivity off the ROLE (`onControl` in
  // usePassageGesture.ts), so a bare onClick on a role="img" would both play
  // the chord AND move the start cursor to its bar — the guitar diagrams
  // never did, because PlayableDiagram's wrapper carries role="button". The
  // role also buys what a button owes anyway: a tab stop and Enter/Space
  // activation.
  const interactive = onKeyClick
    ? { role: 'group' as const }
    : onClick
      ? {
          role: 'button' as const,
          tabIndex: 0,
          onClick,
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          },
        }
      : { role: 'img' as const };
  // The ratio rides a custom property so a stylesheet can size the strip
  // by height and let the width follow (`.piano-chord svg`).
  const style = {
    ...(onClick || onKeyClick ? { cursor: 'pointer' as const } : {}),
    ['--keyboard-aspect' as string]: String(viewW / H),
  } as React.CSSProperties;

  return (
    <svg
      viewBox={`0 0 ${viewW} ${H}`}
      {...size}
      style={style}
      preserveAspectRatio="xMinYMin meet"
      aria-label={label ?? `piano voicing ${midis.join(' ')}`}
      data-testid="piano-keyboard-diagram"
      {...interactive}
    >
      {whites.map((midi, i) => (
        <rect
          key={midi}
          data-key={midi}
          data-pressed={pressed.has(midi) || undefined}
          {...keyProps(midi)}
          x={i * whiteW}
          y={0}
          width={whiteW}
          height={H}
          rx={0.8}
          fill="var(--surface)"
          // Full ink, not the hairline token: at phone size the faint key
          // boundaries were most of "hard to read" (user, Aug 2026) — iReal
          // draws solid outlines and so do we now.
          stroke="var(--ink)"
          strokeWidth={0.7}
        />
      ))}
      {/* The grey middle C — iReal's octave anchor, over the bed but under
          the black keys, keeping the key's own outline. The key's opposite
          ink at low opacity, so it greys toward the black keys in any theme.
          It wears no dot of its own: a dot appears only when C4 is played,
          like any key. */}
      {anchorC && (
        <rect
          data-middle-c=""
          pointerEvents="none"
          x={whiteIndexOf(MIDDLE_C) * whiteW}
          y={0}
          width={whiteW}
          height={H}
          rx={0.8}
          fill="var(--ink)"
          fillOpacity={ANCHOR_OPACITY}
          stroke="var(--ink)"
          strokeWidth={0.7}
        />
      )}
      {blacks.map((midi) => {
        const x = whiteIndexOf(midi) * whiteW - whiteW * 0.3;
        return (
          <rect
            key={midi}
            data-key={midi}
            data-pressed={pressed.has(midi) || undefined}
            {...keyProps(midi)}
            x={x}
            y={0}
            width={whiteW * 0.6}
            height={H * 0.62}
            rx={0.8}
            fill="var(--ink)"
            stroke="var(--ink)"
            strokeWidth={0.4}
          />
        );
      })}
      {[...pressed].map((midi) => {
        const black = IS_BLACK[((midi % 12) + 12) % 12];
        const cx = black ? whiteIndexOf(midi) * whiteW : (whiteIndexOf(midi) + 0.5) * whiteW;
        // Low on whichever key it marks (iReal's placement), so a black key's
        // dot sits inside the black key rather than below it.
        const cy = black ? H * 0.62 - 3.6 : H - 4.6;
        // The dot's contrast is decided by the key it sits ON, not by a theme
        // token: dark on a white key, light on a black one. That cannot be
        // broken by a palette where --accent happens to be near-black.
        const ink = black ? 'var(--surface)' : 'var(--ink)';
        return (
          <circle
            key={`d${midi}`}
            data-dot={midi}
            cx={cx}
            cy={cy}
            r={1.9}
            fill={ink}
            pointerEvents="none"
          />
        );
      })}
    </svg>
  );
}
